import { Controller } from "stimulus"
import Email from "../lib/email"
import Honeybadger from "../honeybadger"

// A problem the visitor can fix. `code` matches the data-error of the message
// to show from the page (the cloud app returns the same codes), so the copy
// stays in the page.
class GatewayError extends Error {
  constructor(code) {
    super(code)
    this.code = code
  }
}

export default class GatewayController extends Controller {

  urlBase =  window.jekyllEnv === 'production' ? 'https://app.beekeeperstudio.io' : 'http://localhost:3000'

  static get targets() {
    return ['form', 'buttons', 'loading', 'email', 'formElements', 'thanks', 'error', 'errorMessage']
  }


  getUrlVars() {
    let vars = {};
    let parts = window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi,
      function (m, key, value) {
        vars[key] = value;
      });
    return vars;
  }

  get editionValue() {
    return this.data.get('edition')
  }


  async isUUIDGood(uuid) {
    if (!uuid) return false;
    try {
      const url = `${this.urlBase}/api/downloads/${uuid}.json`
      const response = await fetch(url, { method: 'GET'})
      return response.ok

    } catch (error) {
      console.error(error)
      Honeybadger.setContext({
        uuid
      })
      Honeybadger.notify(error)
      // just to be safe, we don't wanna block people from getting the app
      return true
    }
    
  }

  bypass(event) {
    if (event) event.preventDefault()
    this.formTarget.style.display = 'none';
    this.buttonsTarget.style.display = '';
  }

  getAnalyticsData() {
    const first = JSON.parse(localStorage.getItem('analytics/first') || null)
    const session = JSON.parse(sessionStorage.getItem('analytics/session') || null)
    return {
      first, session
    }
  }

  async submit(event) {
    event.preventDefault()
    this.hideError()
    const email = this.emailTarget.value
    const url = `${this.urlBase}/api/downloads`
    const analytics = this.getAnalyticsData()
    const payload = {
      email, terms: true, edition: this.editionValue,
    }
    if (analytics.first) {
      payload.entrypoint = analytics.first.entrypoint;
      payload.referrer = analytics.first.referrer;
      payload.first_seen = analytics.first.timestamp / 1000
    }

    if (analytics.session) {
      payload.session_entrypoint = analytics.session.entrypoint
      payload.session_referrer = analytics.session.referrer;
      payload.session_first_seen = analytics.session.timestamp / 1000
    }
    try {
      if (!Email.validate(email)) {
        throw new GatewayError('invalid_email')
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      if (response.ok) {
        sessionStorage.setItem('bypass', true)
        this.bypass()
      } else {
        const json = await response.json()
        // A rejected email is the only error the visitor can fix (older
        // versions of the app sent no code, only a friendly_error). Anything
        // else is our bug: report it and let them download anyway.
        if (json.code === 'invalid_email' || (!json.code && json.friendly_error)) {
          throw new GatewayError('invalid_email')
        }
        throw new Error(`app returned ${response.status}: ${json.code || json.error}`)
      }

    } 
    catch (error) {
      console.error(error)
      if (error instanceof GatewayError) {
        this.showError(error.code)
      } else {
        console.error(error)
        Honeybadger.setContext({
          email: this.emailTarget.value
        })
        Honeybadger.notify(error)
        this.bypass()

      }

    }
  }

  showError(code) {
    this.errorMessageTargets.forEach((el) => {
      el.hidden = el.dataset.error !== code
    })
    this.errorTarget.style.display = ''
  }

  hideError() {
    this.errorMessageTargets.forEach((el) => { el.hidden = true })
    this.errorTarget.style.display = 'none'
  }

  async initialize() {
    const uuid = this.getUrlVars()['uuid'] || null
    console.log("checking UUID", uuid)
    const good = await this.isUUIDGood(uuid)
    const alreadyProvidedEmail = sessionStorage.getItem("bypass")
    if (good || alreadyProvidedEmail) {
      this.bypass()
    } else {
      // Turbo restores the page from a snapshot of the live DOM, so after a
      // skip the buttons are still showing; only the form belongs on screen
      this.buttonsTarget.style.display = 'none'
      this.formTarget.style.display = 'block'
      this.emailTarget.focus()
    }
    this.loadingTarget.style.display = 'none'
  }

}